import {
  Control,
  FieldValues,
  FormState,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../form/TextInput';
import PhoneInput from '../../form/PhoneInput';
import Name from '../../form/Name';
import Address from '../../form/Address';
import { StateCode } from '../../../data/models/state-code.model';
import validationUtils from '../../../services/validationUtils';
import addressUtils from '../../../services/addressUtils';
import { Button, Typography } from '@mui/material';
import { formClasses } from '../ElementDialog';
import { MessagingMethod } from '../../../data/models/messaging-method.model';
import SelectMenu from '../../form/SelectMenu';
import CheckboxInput from '../../form/CheckboxInput';

interface BasicInfoFormProps {
  control: Control;
  formState: FormState<any>;
  requiredFieldMessage: string;
  stateCodes: StateCode[];
  messagingMethods: MessagingMethod[];
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<any>;
}

export default function BasicInfoForm({
  control,
  formState,
  requiredFieldMessage,
  stateCodes,
  messagingMethods,
  getValues,
  setValue,
}: BasicInfoFormProps) {
  const { t } = useTranslation('common');

  const copyBillingAddress = (evt: any) => {
    evt.preventDefault();
    addressUtils.copyBillingToBase(getValues(), setValue);
  };

  return (
    <>
      <TextInput
        label={t('providers.provider')}
        name="TransportationProviderName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
        inputProps={{ maxLength: 100 }}
      />
      <TextInput
        label={t('provider.dispatchEmail')}
        name="EmailAddress"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{
          required: requiredFieldMessage,
          pattern: validationUtils.emailValidation(),
        }}
        inputProps={{ maxLength: 255 }}
      />
      <Name
        namePrefix="MainContact"
        control={control}
        formState={formState}
        requiredFieldMessage={requiredFieldMessage}
        firstNameLabel={t('provider.contactFirst') || ''}
        lastNameLabel={t('provider.contactLast') || ''}
      />
      <Button
        href="#"
        className={formClasses.copy}
        variant="contained"
        color="secondary"
        onClick={copyBillingAddress}>
        {t('provider.copyBillingAddress')}
      </Button>
      <Address
        namePrefix=""
        control={control}
        formState={formState}
        requiredFieldMessage={requiredFieldMessage}
        stateCodes={stateCodes}
      />
      <TextInput
        label={t('common.county')}
        name="County"
        required={false}
        control={control}
        errors={formState.errors}
        inputProps={{ maxLength: 100 }}
      />
      <PhoneInput
        label={t('providers.mainPhone')}
        name="MainPhone"
        extensionName="MainPhoneExtension"
        required={true}
        control={control}
        errors={formState.errors}
      />
      <CheckboxInput
        label={t('common.hasSMSConsent')}
        name="HasSMSConsent"
        required={false}
        control={control}
      />
      <PhoneInput
        label={t('providers.dispatchPhone')}
        name="DispatchPhone"
        extensionName="DispatchPhoneExtension"
        required={true}
        control={control}
        errors={formState.errors}
      />
      <SelectMenu
        label={t('provider.messagingMethod')}
        name="MessagingMethodID"
        values={messagingMethods}
        valueAttr="messagingMethodID"
        labelAttr="messagingMethodName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
      />
      <SelectMenu
        label={t('provider.driverMessagingMethod')}
        name="DefaultDriverMessagingMethodID"
        values={messagingMethods}
        valueAttr="messagingMethodID"
        labelAttr="messagingMethodName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
      />
      <Typography className={formClasses.disclaimer}>
        {t('onboard.messaging.disclaimer')}
      </Typography>
      <Typography className={formClasses.disclaimer}>
        {t('onboard.messaging.data')}
      </Typography>
      <Typography className={formClasses.disclaimer}>
        {t('onboard.messaging.rate')}
      </Typography>
      <Typography className={formClasses.disclaimer}>
        {t('onboard.messaging.optOut')}
      </Typography>
      <Typography className={formClasses.disclaimer}>
        <a href="https://americanlogistics.com/privacy/" target="_blank">
          {t('onboard.messaging.privacyLink')}
        </a>
      </Typography>
      <Typography className={formClasses.disclaimer}>
        <a href="https://americanlogistics.com/legal/" target="_blank">
          {t('onboard.messaging.termsLink')}
        </a>
      </Typography>
    </>
  );
}
